<template>
    <div>
        <!-- Group list -->
        <q-table
            :filter="table.search"
            :filter-method="filterTable"
            :rows="groups"
            :columns="table.cols"
            :rows-per-page-options="[table.pagination.rowsPerPage]"
            :class="
                $q.screen.gt.lg
                    ? 'sticky-table groups-table'
                    : 'groups-table__cardmode'
            "
            row-key="id"
            binary-state-sort
            :no-data-label="$t('No data available')"
            :grid="$q.screen.lt.lg"
        >
            <!-- top bar -->
            <template v-slot:top>
                <div
                    :class="$q.screen.gt.md ? 'q-pa-md' : ''"
                    class="full-width"
                >
                    <!-- title -->
                    <div class="col-12">
                        <div class="text-h4 text-weight-medium">
                            {{ $t('Groups') }}
                        </div>
                        <div class="page-description">
                            {{
                                $t('Manage asset groups for {facilityName}.', {
                                    facilityName: facility?.name
                                })
                            }}
                        </div>
                    </div>
                    <!-- search bar -->
                    <div
                        class="row fit"
                        :class="
                            $q.screen.lt.sm
                                ? ''
                                : 'wrap justify-between items-start content-start'
                        "
                    >
                        <q-input
                            v-model="table.search"
                            @clear="table.search = ''"
                            :placeholder="$t('Search Group')"
                            :class="
                                $q.screen.lt.sm
                                    ? 'col-12 q-mb-md'
                                    : 'col q-mr-md'
                            "
                            class="bg-white"
                            debounce="750"
                            clearable
                            outlined
                            dense
                            color="accent"
                        >
                            <template v-slot:prepend>
                                <q-icon name="search" />
                            </template>
                        </q-input>
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'group_create',
                                    params: {
                                        facilityId: facility?.id
                                    }
                                })
                            "
                            :disable="$userCannot('delete', 'groups')"
                            :label="$t('Add group')"
                            class="table-header__button"
                            :class="$q.screen.lt.sm ? 'col-12' : ''"
                            color="accent"
                            size="md"
                        ></q-btn>
                    </div>
                </div>
            </template>

            <!-- facility column -->
            <template v-slot:body-cell-facility="props">
                <q-td :props="props">
                    {{ facility?.name }}
                </q-td>
            </template>
            <!-- assets column -->
            <template v-slot:body-cell-assets="props">
                <q-td :props="props">
                    <q-expansion-item
                        switch-toggle-side
                        :label="$t('Show Assets')"
                    >
                        <q-list v-if="props.row.assets[0] !== null">
                            <q-item
                                dense
                                v-for="asset in props.row.assets"
                                :key="asset"
                            >
                                {{ assetsMap[asset] }}
                            </q-item>
                        </q-list>
                    </q-expansion-item>
                </q-td>
            </template>
            <!-- actions column -->
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md">
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'group_update',
                                    params: {
                                        facilityId: facility?.id || facilityId,
                                        groupId: props.row.id
                                    }
                                })
                            "
                            class="table__action-button"
                            color="accent"
                            icon="fas fa-edit"
                            outline
                        />
                        <div>
                            <q-btn
                                @click="remove(props.row)"
                                :disable="$userCannot('delete', 'groups')"
                                :color="
                                    $userCannot('delete', 'groups')
                                        ? 'grey'
                                        : 'negative'
                                "
                                class="table__action-button"
                                icon="fas fa-trash-alt"
                                outline
                            />
                            <q-tooltip v-if="$userCannot('delete', 'groups')">
                                {{
                                    $t(
                                        'You do not have permission to perform this action.'
                                    )
                                }}
                            </q-tooltip>
                        </div>
                    </div>
                </q-td>
            </template>

            <!-- Card template -->
            <template v-slot:item="props">
                <div class="col-xs-12 col-sm-6 col-md-4">
                    <group-card
                        @edit="
                            $router.push({
                                name: 'group_update',
                                params: {
                                    groupId: props.row.id
                                }
                            })
                        "
                        @delete="remove(props.row)"
                        :name="props.row.name"
                        :facilityId="props.row.facility"
                        :assets="props.row.assets"
                        :can-edit="$userCan('edit', 'groups')"
                        :can-delete="$userCan('delete', 'groups')"
                    />
                </div>
            </template>
        </q-table>
    </div>
</template>

<script>
import GroupCard from '@components/GroupCard.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { useSharedStore } from '@stores/shared.js';
import { useFacilitiesStore } from '@stores/facilities';
import { useAssetsStore } from '@stores/assets';
import { mapState, mapActions } from 'pinia';

export default {
    name: 'GroupList',
    components: { GroupCard },
    mixins: [BreadcrumbsManager, UpdateURL],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    async mounted() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility?.name || '',
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility?.id || this.facilityId
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility?.id || this.facilityId
                    }
                }
            },
            {
                label: this.$t('Groups')
            }
        ]);
    },
    data() {
        return {
            baseURL: 'assetgroups/',
            url: 'assetgroups/',
            // table configs
            table: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'facility',
                        name: 'facility',
                        label: this.$t('Facility')
                    },
                    {
                        align: 'left',
                        field: 'assets',
                        name: 'assets',
                        label: this.$t('Assets')
                    },
                    {
                        align: 'left',
                        field: 'actions',
                        name: 'actions',
                        label: this.$t('Actions')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: ''
            }
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getFacility']),
        ...mapState(useAssetsStore, ['getAssets', 'getAssetGroups']),
        /**
         * Get selected facility.
         * @return {Object} - facility.
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Get asset groups for facility.
         * @return {Array} - Asset groups.
         */
        groups() {
            return this.getAssetGroups(this.facilityId);
        },
        /**
         * Get Assets for facility mapped by ID.
         * @return {Object} - Assets map.
         */
        assetsMap() {
            const facilityAssets = this.getAssets(this.facilityId);
            return facilityAssets.reduce(function (map, obj) {
                map[obj.id] = obj.name;
                return map;
            }, {});
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useAssetsStore, ['removeAssetGroupAction']),
        /**
         * Allow the user to search through the list of groups
         */
        filterTable(rows) {
            return rows.filter(group =>
                group.name
                    .toLowerCase()
                    .includes(this.table.search.toLowerCase())
            );
        },
        /**
         * Delete group.
         * @param {Object} row - group from table row.
         */
        async remove(row) {
            this.$confirmDelete(
                this.$t('Are you sure you want to delete this group?')
            ).onOk(async () => {
                // begin loading
                this.beginLoading(`${this.$t('Removing group')}...`);
                await this.$axios
                    .delete(`${this.baseURL}${row.id}/`)
                    .then(response => {
                        // remove group from the store
                        this.removeAssetGroupAction(row.id);

                        // notify user
                        this.$notifySuccess(this.$t('Group deleted'));
                    })
                    .catch(error => {
                        this.$notifyError(
                            this.$t(
                                'Failed to remove group. Please try again later.'
                            ),
                            error
                        );
                    })
                    .finally(() => {
                        this.stopLoading();
                    });
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.groups-table {
    height: 80vh;
}

// align table top with cards
.groups-table__cardmode :deep(.q-table__top) {
    padding: 0px;
}
</style>
